import { fmt, limitUsage } from '@k2pos/core';
import { useNavigate, useParams } from 'react-router-dom';
import { HazardStripes } from '@/components/brand/HazardStripes';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { useGoBack } from '@/hooks/useNav';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './CustomerDetail.module.css';

/** One customer's book: what they owe, against what limit, and how it got there. */
export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack('/debt');
  const customer = usePos((s) => s.customers.find((c) => c.id === id));
  const historyOf = usePos((s) => s.historyOf);
  const history = historyOf(id ?? '');

  if (!customer) {
    return <AppBar title="ບໍ່ພົບລູກຄ້າ" onBack={goBack} />;
  }

  const usage = limitUsage(customer);

  return (
    <>
      <AppBar
        title={customer.name}
        onBack={goBack}
        subtitle={
          <Mono size={11} color={colors.onDarkFaint}>
            {customer.phone}
          </Mono>
        }
      />

      <div className={`${styles.body} vscroll`}>
        <div className={styles.balance}>
          <HazardStripes className={styles.stripes} opacity={0.35} period={18} />
          <Text size={12} color={colors.onDarkBright}>
            ຕິດຢູ່ທັງໝົດ
          </Text>
          <Mono weight={700} size={28} color={colors.sky} className={`${styles.amount} selectable`}>
            {fmt(customer.balance)}
          </Mono>
          <Text size={11} color={colors.onDarkSoft}>
            ໜີ້ເກົ່າສຸດ {customer.days} ມື້ · ວົງເງິນ {fmt(customer.limit)} ₭
          </Text>

          <div
            className={styles.limitTrack}
            role="progressbar"
            aria-valuenow={Math.round(usage * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="ວົງເງິນທີ່ໃຊ້ແລ້ວ"
          >
            <div className={styles.limitFill} style={{ width: `${Math.round(usage * 100)}%` }} />
          </div>
        </div>

        <Button
          label="ຮັບຊຳລະ"
          variant="accent"
          disabled={customer.balance === 0}
          onPress={() => navigate(`/receive/${customer.id}`)}
        />

        {/* A link, not a button: the WebView hands tel: to the dialler and a
            browser does the same. Routing it through JS would dead-end.

            There was a ໃບສະຫຼຸບ button beside this one. It navigated to /report —
            the shop-wide end-of-day close, which knows nothing about this
            customer — so it was removed rather than left as a plausible-looking
            dead end. The history list below already is this customer's statement;
            a sendable one is a separate piece of work. */}
        <a href={`tel:${customer.phone.replace(/\s/g, '')}`} className={styles.callLink}>
          <Text weight={800} size={15} color={colors.navy} align="center">
            ທວງ
          </Text>
        </a>

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ປະຫວັດ
        </Text>
        {history.map((entry) => (
          <Card key={entry.id} className={styles.historyRow}>
            <div className="grow col">
              <Text size={12.5} lines={2}>
                {entry.detail}
              </Text>
              <Mono size={10.5} color={colors.textFaint}>
                {entry.date}
              </Mono>
            </div>
            <Mono
              weight={700}
              size={13}
              color={entry.kind === 'credit' ? colors.skyDeep : colors.navy}
              className="selectable"
            >
              {entry.kind === 'credit' ? '+' : '−'}
              {fmt(entry.amount)}
            </Mono>
          </Card>
        ))}

        {history.length === 0 ? (
          <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
            ຍັງບໍ່ມີລາຍການ
          </Text>
        ) : null}
      </div>
    </>
  );
}
